<script setup lang="ts">
import { computed } from "vue";
import { responsiveClass } from "../../utilities/helpers";
import type { ISliderDotsEmits, ISliderDotsProps } from "./VSliderDots.types";

const props = withDefaults(defineProps<ISliderDotsProps>(), {
  size: "s",
});
const emit = defineEmits<ISliderDotsEmits>();

const dotsClass = computed(() => [
  ...responsiveClass("pl-slider-dots_size", props.size),
]);
const handleSet = (idx: number) => emit("update:modelValue", idx);
const handlePrev = () => {
  const idx = props.modelValue > 0 ? props.modelValue - 1 : props.count - 1;
  emit("update:modelValue", idx);
};
const handleNext = () => {
  const idx = props.modelValue < props.count - 1 ? props.modelValue + 1 : 0;
  emit("update:modelValue", idx);
};
const dotSize = computed(() => (props.size === "s" ? 8 : 14));
const dotSelectedSize = computed(() => (props.size === "s" ? 12 : 20));
const innerWidth = computed(
  () => (props.visibleCount - 1) * dotSize.value + dotSelectedSize.value,
);
const offset = computed(() => {
  if (props.visibleCount === props.count || props.modelValue < 2) return "auto";

  const restPoints = props.count - props.modelValue + 1;
  const restOffset = restPoints * dotSize.value;

  if (restOffset > innerWidth.value) {
    return `-${(props.modelValue - 1) * dotSize.value}px`;
  }

  return `-${(props.count - props.visibleCount) * dotSize.value}px`;
});
</script>

<template>
  <div class="pl-slider-dots" :class="dotsClass">
    <div class="pl-slider-dots__side" @click="handlePrev" />
    <div class="pl-slider-dots__inner" :style="{ width: `${innerWidth}px` }">
      <div class="pl-slider-dots__inner-flex">
        <div
          v-for="idx in Number(count)"
          :key="idx"
          class="pl-slider-dots__dot"
          :class="{
            'is-selected': idx - 1 == modelValue,
            'is-prev': idx - 1 < modelValue,
            'is-next': idx - 1 > modelValue,
          }"
          @click="handleSet(idx - 1)"
        />
      </div>
    </div>
    <div class="pl-slider-dots__side" @click="handleNext" />
  </div>
</template>

<style lang="postcss" scoped>
.pl-slider-dots {
  display: flex;
  margin: 12px 0 8px;
  justify-content: center;

  --_slider-dots-s-width: 4px;
  --_slider-dots-s-selected-width: 8px;
  --_slider-dots-s-height: 4px;
  --_slider-dots-s-margin: 0 4px 0 0;
  --_slider-dots-l-width: 6px;
  --_slider-dots-l-selected-width: 12px;
  --_slider-dots-l-height: 6px;
  --_slider-dots-l-margin: 0 8px 0 0;

  @each $size in s, l {
    &_size-$(size) {
      --_slider-dots-width: var(--_slider-dots-$(size)-width);
      --_slider-dots-selected-width: var(--_slider-dots-$(size)-selected-width);
      --_slider-dots-height: var(--_slider-dots-$(size)-height);
      --_slider-dots-margin: var(--_slider-dots-$(size)-margin);
    }
  }

  &__side {
    cursor: pointer;
    flex-grow: 1;
  }

  &__inner {
    overflow: hidden;
  }

  &__inner-flex {
    cursor: pointer;
    position: relative;
    left: v-bind(offset);
    display: inline-flex;
  }

  &__dot {
    transition: all ease-out 0.3s;
    background-color: var(--pl-sliderdots-fill-default);
    border-radius: 16px;
    width: var(--_slider-dots-width);
    height: var(--_slider-dots-height);
    margin: var(--_slider-dots-margin);

    &.is-selected {
      background-color: var(--pl-sliderdots-fill-selected);
      width: var(--_slider-dots-selected-width);
    }
  }
}
</style>
